import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, MessageCircle, Pencil, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatRupiah, formatApiError } from "@/lib/format";
import { useSettings } from "@/context/SettingsContext";
import { waLink } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import DocumentPaper from "@/components/DocumentPaper";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const QuotationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [doc, setDoc] = useState(null);

  const load = () => api.get(`/quotations/${id}`).then((r) => setDoc(r.data)).catch(() => navigate("/quotations"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!doc) return <div className="space-y-4"><Skeleton className="h-10 w-40" /><Skeleton className="h-96 w-full max-w-3xl mx-auto" /></div>;

  const changeStatus = async (s) => {
    try { await api.patch(`/quotations/${id}/status`, null, { params: { status: s } }); toast.success("Status diperbarui"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const convert = async () => {
    try { const res = await api.post(`/quotations/${id}/convert`); toast.success("Dikonversi ke invoice"); navigate(`/invoices/${res.data.id}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendWA = () => {
    const msg = `Halo ${doc.customer_name},\n\nBerikut penawaran *${doc.number}* dari ${settings?.business_name || "kami"}.\nTotal: *${formatRupiah(doc.grand_total)}*\n\nTerima kasih.`;
    const link = waLink(doc.customer_whatsapp, msg);
    if (!link) { toast.error("Customer tidak memiliki nomor WhatsApp"); return; }
    window.open(link, "_blank");
  };

  return (
    <div className="space-y-6">
      <div className="no-print flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" onClick={() => navigate("/quotations")} className="-ml-2"><ArrowLeft className="mr-2 h-4 w-4" />Kembali</Button>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={doc.status} onValueChange={changeStatus}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Disetujui</SelectItem>
              <SelectItem value="rejected">Ditolak</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => navigate(`/quotations/${id}/edit`)}><Pencil className="mr-2 h-4 w-4" />Edit</Button>
          <Button variant="outline" onClick={convert} disabled={!!doc.converted_invoice_id} data-testid="convert-btn"><ArrowRightLeft className="mr-2 h-4 w-4" />Ke Invoice</Button>
          <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10" onClick={sendWA}><MessageCircle className="mr-2 h-4 w-4" />WhatsApp</Button>
          <Button onClick={() => window.print()} data-testid="print-pdf-btn"><Printer className="mr-2 h-4 w-4" />Cetak / PDF</Button>
        </div>
      </div>
      <DocumentPaper doc={doc} settings={settings} type="quotation" />
    </div>
  );
};

export default QuotationView;
